import { SFX } from '../platform/audio';
import { elStage } from '../platform/dom';
import { camWheel } from './camera';
import { STATE } from './fx';
import { togglePause } from '../ui/hud';
import { openCodex } from '../ui/codex';
import { chooseReward } from '../ui/progress';
import { chooseUpgrade, doReroll } from '../ui/levelup';
import { elUp } from '../platform/dom';
/* ============================================================
   輸入
   ============================================================ */
/* 模擬讀的唯一輸入來源。瀏覽器的鍵鼠事件寫進來，測試也直接寫這個物件 ——
   updatePlayer 不再認識鍵盤，這同時解鎖了重播、bot 與日後的網路同步。
   dash / mid / ult 是「本步觸發一次」的旗標，被 updatePlayer 消費後歸零。 */
export var input = { moveX: 0, moveZ: 0, dash: false, mid: false, ult: false, lock: false, lockBoss: false, thrust: false, freeze: false };
/* `freeze`：**輸入被別的東西接管了**（目前只有自由視角 `render/flycam.ts`）。
   WASD 同時是機體的移動鍵，不凍結的話一邊飛一邊把機體開進牆裡。
   旗標放在這裡而不是讓 `sim/` 去 import `render/` —— 那個方向的相依會把
   模擬層綁死在瀏覽器上，`tools/lint-sim.js` 守的就是這件事。
   ⚠️ 它只擋**瀏覽器輸入**，不擋 bot 與測試（那些直接寫 `input` 物件）。 */
/* `thrust` 與其他旗標不同：**它是持續狀態不是事件**（按住多久就飛多久），
   所以在 `syncAxis` 跟著移動軸一起同步，不由模擬清成 false。
   衝刺留在空白鍵、推進器在 Shift —— 兩個能力互不干擾，混戰中不會誤觸。 */

/* Tab 的長按偵測。用牆鐘計時在這裡是正確的 —— 玩家按住多久本來就是真實時間，
   而它被轉成一個離散旗標之後才進入模擬，模擬本身仍然只讀旗標。 */
/** 會打出東西來的鍵。自由視角期間整組擋掉（見 `input.freeze`）。 */
var ACTION_KEYS = [' ', 'j', 'k', 'tab'];

var TAB_HOLD_MS = 320;
var tabTimer: any = 0, tabFired = false;

export var keys = {};
export function syncAxis() {
  var x = 0, z = 0;
  if (input.freeze) { input.moveX = 0; input.moveZ = 0; input.thrust = false; return; }
  if (keys['a'] || keys['arrowleft']) x -= 1;
  if (keys['d'] || keys['arrowright']) x += 1;
  if (keys['w'] || keys['arrowup']) z -= 1;
  if (keys['s'] || keys['arrowdown']) z += 1;
  var l = Math.hypot(x, z); if (l > 0) { x /= l; z /= l; }
  input.moveX = x; input.moveZ = z;
  input.thrust = !!keys['shift'];
}

export function initInput() {
  window.addEventListener('keydown', function (e) {   // det-ok: 開機期：瀏覽器事件接線，只在 initInput 執行一次；模擬只讀 input 物件
    // 除錯主控台打字時不要驅動機體
    var tn = (e.target as any) && (e.target as any).tagName;
    if (tn === 'INPUT' || tn === 'TEXTAREA') return;
    if (e.repeat) { e.preventDefault(); return; }
    var k = e.key.toLowerCase(); keys[k] = true; syncAxis();
    /* 自由視角期間只放行「不影響機體」的鍵（音效、暫停、圖鑑照舊可用），
       衝刺／小招／大招／鎖定一律不觸發 —— 飛著看地圖時不該打出東西來。 */
    if (input.freeze && ACTION_KEYS.indexOf(k) >= 0) { e.preventDefault(); return; }
    if (k === ' ') { input.dash = true; e.preventDefault(); }
    if (k === 'j') input.mid = true;
    if (k === 'k') input.ult = true;
    if (k === 'tab') {
      e.preventDefault();
      tabFired = false;
      clearTimeout(tabTimer);   // det-ok: 輸入層：把按住的時長轉成離散旗標，不在模擬路徑上
      tabTimer = setTimeout(function () { input.lockBoss = true; tabFired = true; }, TAB_HOLD_MS);   // det-ok: 同上
    }
    if (k === 'm') SFX.toggle();
    /* 數字鍵選卡。**升級介面與補給台共用 `STATE.reward`**，所以要看哪一個
       畫面真的開著 —— 在這之前一律打給 `chooseReward`，於是升級時按 1
       會拿上一次補給台留下的 `currentRewards` 去裝模組並打開傳送門。
       選項數吃局外部件（最多 3 + GRANT.choices），所以放行到 6。 */
    if (STATE.reward && '123456'.indexOf(k) >= 0) {
      if (elUp && elUp.classList.contains('show')) chooseUpgrade(parseInt(k, 10) - 1);
      else chooseReward(parseInt(k, 10) - 1);
    }
    /* R 重抽（`03-in-run-progression.md` 第四節的能動性工具）。
       只在升級介面開著時有效 —— 排除與重抽都不適用於 Boss 的定向發牌，
       那道判斷在 `ui/levelup.ts` 的 `doReroll` 裡。 */
    if (k === 'r' && STATE.reward && elUp && elUp.classList.contains('show')) doReroll();
    if (k === 'p' && STATE.running && !STATE.reward) togglePause();
    /* 對局中開圖鑑（使用者 2026-08-02 指定的第二個入口）。
       **會順便暫停** —— 一邊被三百隻圍著一邊查資料不是一個真實的情境，
       而且圖鑑要接管鏡頭，不暫停的話回來時玩家已經死了。 */
    if (k === 'c' && STATE.running && !STATE.reward && !STATE.over) {
      if (!STATE.paused) togglePause();
      openCodex('run');
    }
    if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].indexOf(k) >= 0) e.preventDefault();
  });
  window.addEventListener('keyup', function (e) {   // det-ok: 開機期：同上
    var k = e.key.toLowerCase(); keys[k] = false; syncAxis();
    if (k === 'tab') {
      clearTimeout(tabTimer);   // det-ok: 輸入層：長按偵測的收尾
      if (!tabFired) input.lock = true;   // 沒到長按門檻 → 當成短按的開關
    }
  });
  window.addEventListener('blur', function () { keys = {}; syncAxis(); });   // det-ok: 開機期：同上
  elStage.addEventListener('contextmenu', function (e) { e.preventDefault(); });
  /* 滾輪縮放鏡頭。**這是把品味判斷交還給手感** —— 鏡頭高度我調不出
     「剛剛好」，但玩的人三秒就滾到了，滾完用主控台的 `cam` 讀出數字。 */
  window.addEventListener('wheel', function (e) { if (!input.freeze) camWheel(e.deltaY); }, { passive: true });   // det-ok: 開機期：瀏覽器事件接線
  elStage.addEventListener('mousedown', function (e) {
    if (input.freeze) return;             // 自由視角時滑鼠是「轉鏡頭」，不是開火
    if (e.button === 0) input.mid = true;
    if (e.button === 1) { input.lock = true; e.preventDefault(); }
    if (e.button === 2) input.ult = true;
  });
}
